import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { KeyRound, Mail, Lock, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { loginWithMicrosoft, loginWithCredentials, isAuthenticated } = useAuth();

  const [showLocalLogin, setShowLocalLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate({ to: '/' });
    return null;
  }

  const handleMicrosoftLogin = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await loginWithMicrosoft();
      navigate({ to: '/' });
    } catch (err) {
      setError('Microsoft sign-in failed. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLocalLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await loginWithCredentials(email, password);
      navigate({ to: '/' });
    } catch (err) {
      setError('Invalid email or password.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        {/* Logo/Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <KeyRound className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-grey-800">ATA Contract Manager</h1>
          <p className="text-sm text-grey-500 mt-1">IT Contract Management System</p>
        </div>

        <Card>
          <CardContent className="pt-6">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Microsoft SSO - Primary */}
            <Button
              className="w-full h-12 text-base gap-3"
              onClick={handleMicrosoftLogin}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                </svg>
              )}
              Sign in with Microsoft
            </Button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs">
                <button
                  type="button"
                  className="bg-surface px-3 text-grey-400 hover:text-grey-600 flex items-center gap-1 transition-colors"
                  onClick={() => setShowLocalLogin(!showLocalLogin)}
                >
                  {showLocalLogin ? (
                    <>
                      Hide email sign-in <ChevronUp className="h-3 w-3" />
                    </>
                  ) : (
                    <>
                      Sign in with email <ChevronDown className="h-3 w-3" />
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Local Login - Collapsible Fallback */}
            {showLocalLogin && (
              <form onSubmit={handleLocalLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@ata.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-9"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="outline"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Sign in
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-grey-400 mt-6">
          ATA Digital &middot; IT Department
        </p>
      </div>
    </div>
  );
};

export default Login;
